/**
 * Mg Theme — Real Header Height Publisher
 * (c) 2026 NAMY SOLUTIONS LLC
 *
 * Measures mg-header + mg-announcement-bar live and publishes the summed
 * pixel height as `--mg-real-header-h` on :root. CSS consumers (scroll-
 * padding-top for WCAG 2.4.11, fit-viewport hero block-size) read this var
 * with a token-calc fallback.
 *
 * Canonical 2026 pattern (per MDN + web.dev): ResizeObserver is load-bearing.
 * It fires on initial layout, viewport resize, orientation rotation, content
 * reflow, font swap that mutates box. `document.fonts.ready` covers webfont
 * swap deterministically (belt-and-braces). `resize`/`orientationchange`
 * (deprecated)/`load` listeners are redundant subsets of RO coverage.
 *
 * Idempotent: window.__namyRealHeaderInit guard prevents double-init on
 * Shopify editor hot-reload.
 *
 * Reference:
 */
(function() {
  if (window.__namyRealHeaderInit) return;
  window.__namyRealHeaderInit = true;

  function measure() {
    var h = document.querySelector('mg-header');
    var a = document.querySelector('.mg-announcement-bar, mg-announcement-bar');
    var total = (h ? h.offsetHeight : 0) + (a ? a.offsetHeight : 0);
    if (total > 0) {
      document.documentElement.style.setProperty('--mg-real-header-h', total + 'px');
    }
  }

  /* rAF coalesce : multiple events firing in the same frame (e.g. iOS Safari
     address-bar scroll spam triggering both resize + RO) collapse to ONE
     measure call. Avoids layout-thrash + redundant DOM reads. */
  var rafId = null;
  function scheduleMeasure() {
    /* Always SUPERSEDE a pending frame — never early-return on a stuck rafId.
       In a BACKGROUND/hidden tab requestAnimationFrame is PAUSED, so a rafId
       queued there never fires; an `if (rafId) return` would then permanently
       block every later schedule (incl. the visibilitychange/pageshow re-measure
       on return) → --mg-real-header-h stays UNSET → the hero falls back to the
       header token height (wrong) and only "descales" into place when the tab is
       refocused. Root cause of the reported tab-revisit instability. */
    if (rafId) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
    if (document.hidden) {
      /* rAF won't fire while hidden — measure synchronously. Layout + offsetHeight
         are available in background tabs (only paint/rAF are throttled), so this
         sets the var correctly even on a background load. */
      measure();
    } else {
      rafId = requestAnimationFrame(function() {
        rafId = null;
        measure();
      });
    }
  }

  function init() {
    var h = document.querySelector('mg-header');
    var a = document.querySelector('.mg-announcement-bar, mg-announcement-bar');
    if (!('ResizeObserver' in window)) return measure(); // legacy fallback
    var ro = new ResizeObserver(scheduleMeasure);
    if (h) ro.observe(h);
    if (a) ro.observe(a);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(scheduleMeasure);

    /* RO doesn't reliably fire on (a) tab return from hidden state,
       (b) bfcache restore, (c) iOS Safari address-bar viewport changes
       that don't mutate header offsetHeight. Belt-and-braces explicit
       listeners cover these gaps. All routed through rAF coalesce so
       sync spam from iOS resize collapses to 1 measure/frame. */
    document.addEventListener('visibilitychange', function() {
      if (!document.hidden) scheduleMeasure();
    });
    window.addEventListener('pageshow', scheduleMeasure);
    window.addEventListener('resize', scheduleMeasure, { passive: true });
    /* orientationchange is deprecated + redundant with resize — skip. */

    /* Explicit initial measure — do NOT rely on the ResizeObserver's first
       callback firing (RO notifications are tied to the rendering steps, which
       are throttled in background tabs just like rAF). scheduleMeasure() handles
       the hidden case synchronously, so the var is set on a background load too. */
    scheduleMeasure();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
